import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Calendar, Clock, Send, Loader2, Plus, Trash2, Pause, Play,
  RotateCcw, ChevronLeft, Users, CheckCircle, AlertTriangle, X,
  Repeat, Zap, Eye, History, Filter,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  scheduled: { bg: "bg-blue-500/10", text: "text-blue-600", border: "border-blue-500/20", label: "Scheduled" },
  paused: { bg: "bg-amber-500/10", text: "text-amber-600", border: "border-amber-500/20", label: "Paused" },
  sending: { bg: "bg-purple-500/10", text: "text-purple-600", border: "border-purple-500/20", label: "Sending..." },
  sent: { bg: "bg-emerald-500/10", text: "text-emerald-600", border: "border-emerald-500/20", label: "Sent" },
  failed: { bg: "bg-red-500/10", text: "text-red-600", border: "border-red-500/20", label: "Failed" },
  cancelled: { bg: "bg-gray-500/10", text: "text-gray-500", border: "border-gray-500/20", label: "Cancelled" },
};

const AUDIENCE_LABELS = { all: "All Users", role: "By Role", activity: "By Activity", subscribers: "Newsletter Subscribers", custom: "Custom Filter" };
const RECURRENCE_LABELS = { one_time: "One Time", annual: "Annual" };
const USER_ROLES = ["student", "teacher", "parent", "school_admin", "researcher", "other"];

export function ScheduledMessagesManager({ darkMode: dm }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(null); // null | "new" | msg_id
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewSample, setPreviewSample] = useState([]);
  const [filter, setFilter] = useState("all"); // all | scheduled | sent | paused
  const [viewLog, setViewLog] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem("gmp_token")}` };

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/scheduled-messages`, { headers });
      setMessages(res.data);
    } catch (err) {
      toast.error("Failed to load scheduled messages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const resetForm = () => ({
    title: "", body_html: "", template_type: "admin_notification", subject: "",
    schedule_date: "", schedule_end_date: "", schedule_time: "08:00", schedule_time_end: "",
    recurrence: "one_time", audience_type: "all", audience_filter: {},
  });

  const openNew = () => { setForm(resetForm()); setEditMode("new"); setPreviewCount(null); };

  const openEdit = (msg) => {
    setForm({
      title: msg.title, body_html: msg.body_html, template_type: msg.template_type,
      subject: msg.subject, schedule_date: msg.schedule_date, schedule_end_date: msg.schedule_end_date || "",
      schedule_time: msg.schedule_time, schedule_time_end: msg.schedule_time_end || "",
      recurrence: msg.recurrence, audience_type: msg.audience_type, audience_filter: msg.audience_filter || {},
    });
    setEditMode(msg.id);
    setPreviewCount(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editMode === "new") {
        const res = await axios.post(`${API}/admin/scheduled-messages`, form, { headers });
        toast.success(`Message scheduled! Next run: ${res.data.next_run_at ? new Date(res.data.next_run_at).toLocaleString() : "N/A"}`);
      } else {
        await axios.put(`${API}/admin/scheduled-messages/${editMode}`, form, { headers });
        toast.success("Message updated");
      }
      setEditMode(null);
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this scheduled message?")) return;
    try {
      await axios.delete(`${API}/admin/scheduled-messages/${id}`, { headers });
      toast.success("Deleted");
      fetchMessages();
    } catch { toast.error("Failed to delete"); }
  };

  const handlePause = async (id) => {
    try {
      await axios.post(`${API}/admin/scheduled-messages/${id}/pause`, {}, { headers });
      toast.success("Message paused");
      fetchMessages();
    } catch { toast.error("Failed to pause"); }
  };

  const handleResume = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/scheduled-messages/${id}/resume`, {}, { headers });
      toast.success(`Resumed! Next run: ${res.data.next_run_at ? new Date(res.data.next_run_at).toLocaleString() : "N/A"}`);
      fetchMessages();
    } catch { toast.error("Failed to resume"); }
  };

  const handleSendNow = async (id) => {
    if (!window.confirm("Send this message now to the full audience?")) return;
    try {
      const res = await axios.post(`${API}/admin/scheduled-messages/${id}/send-now`, {}, { headers });
      toast.success(`Sent! ${res.data.success}/${res.data.total} delivered`);
      fetchMessages();
    } catch { toast.error("Send failed"); }
  };

  const previewAudience = async () => {
    try {
      const res = await axios.post(`${API}/admin/scheduled-messages/preview-audience`, {
        audience_type: form.audience_type, audience_filter: form.audience_filter,
      }, { headers });
      setPreviewCount(res.data.count);
      setPreviewSample(res.data.sample || []);
    } catch { toast.error("Failed to preview audience"); }
  };

  const filtered = messages.filter(m => filter === "all" || m.status === filter);

  // ──── EDITOR VIEW ────
  if (editMode !== null) {
    return (
      <div className="space-y-4" data-testid="scheduled-message-editor">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setEditMode(null)} className={`rounded-xl ${dm ? "text-gray-300" : ""}`} data-testid="back-from-editor-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h3 className={`text-lg font-bold flex-1 ${dm ? "text-gray-100" : "text-gray-900"}`}>
            {editMode === "new" ? "New Scheduled Message" : "Edit Scheduled Message"}
          </h3>
          <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-xl bg-[#e0482d] hover:bg-[#c93d25] text-white" data-testid="save-scheduled-msg-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
            {editMode === "new" ? "Schedule Message" : "Update"}
          </Button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-4">
            {/* Content */}
            <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
              <CardHeader className="pb-2"><CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>Message Content</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-600"}`}>Title</label>
                  <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g., Christmas Greeting" className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`} data-testid="msg-title-input" />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-600"}`}>Subject Line</label>
                  <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="Email subject..." className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`} data-testid="msg-subject-input" />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-600"}`}>Template Type</label>
                  <div className="flex gap-2">
                    {["admin_notification", "newsletter"].map(t => (
                      <button key={t} onClick={() => setForm(p => ({ ...p, template_type: t }))} className={`px-4 py-2 rounded-xl text-xs font-medium transition-all ${form.template_type === t ? "bg-[#e0482d] text-white" : (dm ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200")}`}>
                        {t === "admin_notification" ? "System Notification" : "Newsletter"}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-600"}`}>
                    Message Body <span className={`text-[10px] ${dm ? "text-gray-500" : "text-gray-400"}`}>(HTML supported, use {"{{name}}"} for personalization)</span>
                  </label>
                  <textarea
                    value={form.body_html}
                    onChange={e => setForm(p => ({ ...p, body_html: e.target.value }))}
                    rows={8}
                    className={`w-full rounded-xl border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${dm ? "bg-gray-900 border-gray-600 text-gray-200" : "bg-white border-gray-200 text-gray-800"}`}
                    placeholder={"Hello {{name}},\n\nYour message content here..."}
                    data-testid="msg-body-textarea"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Schedule */}
            <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
              <CardHeader className="pb-2"><CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>Schedule</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className={`block text-xs font-medium mb-1.5 ${dm ? "text-gray-300" : "text-gray-600"}`}>Recurrence</label>
                  <div className="flex gap-2">
                    {["one_time", "annual"].map(r => (
                      <button key={r} onClick={() => setForm(p => ({ ...p, recurrence: r }))} className={`px-4 py-2 rounded-xl text-xs font-medium flex items-center gap-1.5 transition-all ${form.recurrence === r ? "bg-[#e0482d] text-white" : (dm ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200")}`}>
                        {r === "one_time" ? <Zap className="w-3 h-3" /> : <Repeat className="w-3 h-3" />}
                        {RECURRENCE_LABELS[r]}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-600"}`}>
                      {form.recurrence === "annual" ? "Date (MM-DD)" : "Date (YYYY-MM-DD)"}
                    </label>
                    <Input
                      type={form.recurrence === "annual" ? "text" : "date"}
                      value={form.schedule_date}
                      onChange={e => setForm(p => ({ ...p, schedule_date: e.target.value }))}
                      placeholder={form.recurrence === "annual" ? "12-25" : "2026-12-25"}
                      className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                      data-testid="msg-date-input"
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-600"}`}>Time (GMT)</label>
                    <Input type="time" value={form.schedule_time} onChange={e => setForm(p => ({ ...p, schedule_time: e.target.value }))} className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`} data-testid="msg-time-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-600"}`}>End Date (optional range)</label>
                    <Input
                      type={form.recurrence === "annual" ? "text" : "date"}
                      value={form.schedule_end_date}
                      onChange={e => setForm(p => ({ ...p, schedule_end_date: e.target.value }))}
                      placeholder="Optional"
                      className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-600"}`}>End Time (optional window)</label>
                    <Input type="time" value={form.schedule_time_end || ""} onChange={e => setForm(p => ({ ...p, schedule_time_end: e.target.value }))} className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Audience */}
          <div className="space-y-4">
            <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
              <CardHeader className="pb-2"><CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}><Users className="w-4 h-4 inline mr-1" /> Target Audience</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 gap-1.5">
                  {["all", "role", "activity", "subscribers", "custom"].map(a => (
                    <button key={a} onClick={() => setForm(p => ({ ...p, audience_type: a, audience_filter: a === "all" || a === "subscribers" ? {} : p.audience_filter }))} className={`px-3 py-2 rounded-lg text-xs font-medium text-left transition-all ${form.audience_type === a ? "bg-[#e0482d] text-white" : (dm ? "bg-gray-700/50 text-gray-300 hover:bg-gray-700" : "bg-gray-50 text-gray-700 hover:bg-gray-100")}`} data-testid={`audience-${a}-btn`}>
                      {AUDIENCE_LABELS[a]}
                    </button>
                  ))}
                </div>

                {(form.audience_type === "role" || form.audience_type === "custom") && (
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${dm ? "text-gray-300" : "text-gray-600"}`}>Select Roles</label>
                    <div className="flex flex-wrap gap-1.5">
                      {USER_ROLES.map(role => {
                        const selected = (form.audience_filter?.roles || []).includes(role);
                        return (
                          <button key={role} onClick={() => {
                            const roles = form.audience_filter?.roles || [];
                            const newRoles = selected ? roles.filter(r => r !== role) : [...roles, role];
                            setForm(p => ({ ...p, audience_filter: { ...p.audience_filter, roles: newRoles } }));
                          }} className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all ${selected ? "bg-[#e0482d] text-white" : (dm ? "bg-gray-700 text-gray-400 hover:text-gray-200" : "bg-gray-100 text-gray-600 hover:bg-gray-200")}`}>
                            {role.replace("_", " ")}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(form.audience_type === "activity" || form.audience_type === "custom") && (
                  <div className="space-y-2">
                    <label className={`block text-xs font-medium ${dm ? "text-gray-300" : "text-gray-600"}`}>Activity Filter</label>
                    <div className="flex gap-2">
                      {["active", "inactive"].map(c => (
                        <button key={c} onClick={() => setForm(p => ({ ...p, audience_filter: { ...p.audience_filter, condition: c } }))} className={`px-3 py-1.5 rounded-lg text-[11px] font-medium ${(form.audience_filter?.condition || "active") === c ? "bg-[#e0482d] text-white" : (dm ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-600")}`}>
                          {c === "active" ? "Active Users" : "Inactive Users"}
                        </button>
                      ))}
                    </div>
                    <div>
                      <label className={`block text-[11px] mb-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>Days threshold</label>
                      <Input type="number" min={1} value={form.audience_filter?.inactive_days || ""} onChange={e => setForm(p => ({ ...p, audience_filter: { ...p.audience_filter, inactive_days: parseInt(e.target.value) || "" } }))} placeholder="e.g., 14" className={`rounded-xl h-8 text-xs ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`} data-testid="inactive-days-input" />
                    </div>
                  </div>
                )}

                <Button variant="outline" size="sm" className={`w-full rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} onClick={previewAudience} data-testid="preview-audience-btn">
                  <Eye className="w-3.5 h-3.5 mr-1" /> Preview Audience
                </Button>
                {previewCount !== null && (
                  <div className={`p-3 rounded-xl ${dm ? "bg-gray-700/50" : "bg-gray-50"}`} data-testid="audience-preview-result">
                    <p className={`text-sm font-semibold ${dm ? "text-gray-100" : "text-gray-900"}`}>{previewCount} recipient{previewCount !== 1 ? "s" : ""}</p>
                    {previewSample.length > 0 && (
                      <p className={`text-[11px] mt-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>
                        Sample: {previewSample.slice(0, 5).join(", ")}{previewSample.length > 5 ? "..." : ""}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ──── LIST VIEW ────
  return (
    <div className="space-y-4" data-testid="scheduled-messages-list">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>Scheduled Messages</h3>
          <p className={`text-xs mt-0.5 ${dm ? "text-gray-400" : "text-gray-500"}`}>
            Automate notifications, seasonal greetings, and newsletters
          </p>
        </div>
        <Button size="sm" onClick={openNew} className="rounded-xl bg-[#e0482d] hover:bg-[#c93d25] text-white" data-testid="new-scheduled-msg-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Message
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-1.5">
        {["all", "scheduled", "sent", "paused", "failed"].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f ? "bg-[#e0482d] text-white" : (dm ? "bg-gray-700/50 text-gray-400 hover:text-gray-200" : "bg-gray-100 text-gray-600 hover:bg-gray-200")}`} data-testid={`filter-${f}-btn`}>
            {f === "all" ? `All (${messages.length})` : `${f.charAt(0).toUpperCase() + f.slice(1)} (${messages.filter(m => m.status === f).length})`}
          </button>
        ))}
      </div>

      {/* Messages */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className={`text-center py-12 rounded-2xl border-2 border-dashed ${dm ? "border-gray-700 text-gray-500" : "border-gray-200 text-gray-400"}`}>
          <Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm font-medium">{filter === "all" ? "No scheduled messages yet" : `No ${filter} messages`}</p>
          <p className="text-xs mt-1">Create your first automated message</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(msg => {
            const st = STATUS_STYLES[msg.status] || STATUS_STYLES.scheduled;
            return (
              <Card key={msg.id} className={`rounded-2xl border transition-all hover:shadow-sm ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`} data-testid={`scheduled-msg-${msg.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Left: Schedule info */}
                    <div className={`w-16 h-16 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${dm ? "bg-gray-700/50" : "bg-gray-50"}`}>
                      <Calendar className={`w-4 h-4 mb-0.5 ${dm ? "text-gray-400" : "text-gray-500"}`} />
                      <span className={`text-[10px] font-mono ${dm ? "text-gray-300" : "text-gray-700"}`}>{msg.schedule_date}</span>
                      <span className={`text-[9px] ${dm ? "text-gray-500" : "text-gray-400"}`}>{msg.schedule_time} GMT</span>
                    </div>

                    {/* Center: Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className={`font-semibold text-sm truncate ${dm ? "text-gray-100" : "text-gray-900"}`}>{msg.title}</h4>
                        <Badge className={`text-[9px] px-1.5 ${st.bg} ${st.text} ${st.border}`}>{st.label}</Badge>
                        <Badge variant="outline" className={`text-[9px] ${dm ? "border-gray-600 text-gray-400" : ""}`}>
                          {msg.recurrence === "annual" ? <><Repeat className="w-2.5 h-2.5 mr-0.5" /> Annual</> : "One-time"}
                        </Badge>
                      </div>
                      <p className={`text-xs truncate ${dm ? "text-gray-400" : "text-gray-500"}`}>{msg.subject}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className={`text-[10px] flex items-center gap-1 ${dm ? "text-gray-500" : "text-gray-400"}`}>
                          <Users className="w-3 h-3" /> {AUDIENCE_LABELS[msg.audience_type] || msg.audience_type}
                        </span>
                        {msg.send_count > 0 && (
                          <span className={`text-[10px] flex items-center gap-1 ${dm ? "text-gray-500" : "text-gray-400"}`}>
                            <Send className="w-3 h-3" /> Sent {msg.send_count}x
                          </span>
                        )}
                        {msg.next_run_at && msg.status === "scheduled" && (
                          <span className={`text-[10px] flex items-center gap-1 ${dm ? "text-blue-400" : "text-blue-600"}`}>
                            <Clock className="w-3 h-3" /> Next: {new Date(msg.next_run_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {msg.status === "scheduled" && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handlePause(msg.id)} className={`h-8 w-8 p-0 rounded-lg ${dm ? "text-gray-400 hover:text-amber-400" : "text-gray-500 hover:text-amber-600"}`} title="Pause" data-testid={`pause-msg-${msg.id}`}>
                            <Pause className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleSendNow(msg.id)} className={`h-8 w-8 p-0 rounded-lg ${dm ? "text-gray-400 hover:text-green-400" : "text-gray-500 hover:text-green-600"}`} title="Send Now" data-testid={`sendnow-msg-${msg.id}`}>
                            <Zap className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                      {msg.status === "paused" && (
                        <Button variant="ghost" size="sm" onClick={() => handleResume(msg.id)} className={`h-8 w-8 p-0 rounded-lg ${dm ? "text-gray-400 hover:text-green-400" : "text-gray-500 hover:text-green-600"}`} title="Resume" data-testid={`resume-msg-${msg.id}`}>
                          <Play className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openEdit(msg)} className={`h-8 w-8 p-0 rounded-lg ${dm ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-700"}`} title="Edit" data-testid={`edit-msg-${msg.id}`}>
                        <Filter className="w-3.5 h-3.5" />
                      </Button>
                      {(msg.delivery_log || []).length > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => setViewLog(viewLog === msg.id ? null : msg.id)} className={`h-8 w-8 p-0 rounded-lg ${dm ? "text-gray-400 hover:text-blue-400" : "text-gray-500 hover:text-blue-600"}`} title="Delivery Log" data-testid={`log-msg-${msg.id}`}>
                          <History className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(msg.id)} className={`h-8 w-8 p-0 rounded-lg ${dm ? "text-gray-400 hover:text-red-400" : "text-gray-500 hover:text-red-600"}`} title="Delete" data-testid={`delete-msg-${msg.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Delivery Log Expandable */}
                  {viewLog === msg.id && (msg.delivery_log || []).length > 0 && (
                    <div className={`mt-3 pt-3 border-t space-y-1.5 ${dm ? "border-gray-700" : "border-gray-100"}`} data-testid={`delivery-log-${msg.id}`}>
                      <p className={`text-xs font-semibold ${dm ? "text-gray-300" : "text-gray-700"}`}>Delivery Log</p>
                      {(msg.delivery_log || []).map((log, i) => (
                        <div key={i} className={`flex items-center gap-3 text-[11px] p-2 rounded-lg ${dm ? "bg-gray-700/30" : "bg-gray-50"}`}>
                          <span className={dm ? "text-gray-400" : "text-gray-500"}>{new Date(log.sent_at).toLocaleString()}</span>
                          <span className="text-emerald-600">{log.success} sent</span>
                          {log.failed > 0 && <span className="text-red-500">{log.failed} failed</span>}
                          <span className={dm ? "text-gray-500" : "text-gray-400"}>/ {log.total} total</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
